/**
 * Skills tab — competence matrix (or own skills) plus the catalog list.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuthStore } from '@/stores/authStore';
import { SkillsList } from './SkillsList';
import { SkillFormDialog } from './SkillFormDialog';
import { CompetenceMatrix } from './CompetenceMatrix';
import { OwnSkillsView } from './OwnSkillsView';

type SkillSubTab = 'matrix' | 'own' | 'catalog';

export function SkillsTab() {
  const { t } = useTranslation('hr');
  const { t: ts } = useTranslation('settings');
  const canViewLevels = useAuthStore((s) => s.hasPermission('skills.view_levels'));
  const canManageTeam = useAuthStore((s) => s.hasPermission('hr.manage_team'));
  const [searchParams, setSearchParams] = useSearchParams();
  const filterUserId = searchParams.get('skillUser');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [subTab, setSubTab] = useState<SkillSubTab>(canViewLevels ? 'matrix' : 'own');

  useEffect(() => {
    if (filterUserId && canViewLevels) {
      setSubTab('matrix');
    }
  }, [filterUserId, canViewLevels]);

  const clearUserFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('skillUser');
    setSearchParams(next, { replace: true });
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('skillsMatrix.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {canViewLevels ? t('skillsMatrix.subtitle') : t('skillsMatrix.ownSubtitle')}
            </Typography>
          </Box>
          {canManageTeam && subTab === 'catalog' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              {ts('skillsTab.addSkill')}
            </Button>
          )}
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={subTab}
            onChange={(_e, value: SkillSubTab) => setSubTab(value)}
            aria-label={t('skillsMatrix.tabsAria')}
          >
            {canViewLevels ? (
              <Tab value="matrix" label={t('skillsMatrix.matrixTab')} />
            ) : (
              <Tab value="own" label={t('skillsMatrix.ownTab')} />
            )}
            <Tab value="catalog" label={t('skillsMatrix.catalogTab')} />
          </Tabs>
        </Box>

        {subTab === 'matrix' && canViewLevels && (
          <CompetenceMatrix
            filterUserId={filterUserId}
            onClearUserFilter={clearUserFilter}
          />
        )}
        {subTab === 'own' && !canViewLevels && <OwnSkillsView />}
        {subTab === 'catalog' && <SkillsList />}

        {canManageTeam && (
          <SkillFormDialog
            open={createDialogOpen}
            onClose={() => setCreateDialogOpen(false)}
          />
        )}
      </Stack>
    </Box>
  );
}
